// =============================================================================
// NotesToHankChat.tsx — Top-level chat component for the AI tab (web port)
// =============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import {
  NotesToHankProvider,
  useNotesToHank,
} from '../../context/NotesToHankContext/NotesToHankContext';
import { isSendEnabled } from '../../services/notesToHank/notesToHankMessageUtils';
import type { AttachmentItem } from '../../services/notesToHank/NotesToHankTypes';

import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import AttachmentPreview from './AttachmentPreview';
import TypingIndicator from './TypingIndicator';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NotesToHankChatProps {
  dealId: string;
  dealInfo?: any;
}

// ---------------------------------------------------------------------------
// Inner component (consumes context)
// ---------------------------------------------------------------------------

const NotesToHankChatInner: React.FC<{
  dealId: string;
  dealInfo?: any;
}> = () => {
  const { theme } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isWaitingForResponse,
    connectionState,
    inputText,
    setInputText,
    pendingPhotos,
    pendingAttachments,
    selectedAnswerIds,
    pressedActionButtonIds,
    sendMessage,
    selectAnswer,
    pressActionButton,
    addAttachment,
    removeAttachment,
    retryLastMessage,
  } = useNotesToHank();

  const [collapsed, setCollapsed] = useState(messages.length > 3);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Combine photos + file attachments for the preview strip
  const allAttachments = [...pendingPhotos, ...pendingAttachments];

  const canSend = isSendEnabled(
    inputText,
    selectedAnswerIds,
    pressedActionButtonIds
  );
  const sendDisabled = !canSend || isWaitingForResponse;

  const pressedActionSet = new Set(pressedActionButtonIds);

  // ------------------------------------------------------------------
  // File input handler (web replacement for mobile camera/picker)
  // ------------------------------------------------------------------

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAttachPress = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) {
        return;
      }

      Array.from(files).forEach((file) => {
        const url = URL.createObjectURL(file);
        const isImage = file.type.startsWith('image/');

        const attachment: AttachmentItem = {
          uri: url,
          type: isImage ? 'photo' : 'file',
          mimeType: file.type,
          fileName: file.name,
          timestamp: Date.now(),
        };
        addAttachment(attachment);
      });

      // Reset input so the same file can be selected again
      e.target.value = '';
    },
    [addAttachment]
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 400,
        backgroundColor: theme.background1,
      }}
    >
      {/* Connection status banner */}
      {connectionState === 'reconnecting' && (
        <Box
          sx={{
            backgroundColor: theme.primary,
            py: 0.75,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.textCTA, fontWeight: 600 }}
          >
            Reconnecting...
          </Typography>
        </Box>
      )}

      {/* Messages area */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 4,
              py: 3,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.secondary, textAlign: 'center' }}
            >
              Start a conversation with Hank to get AI-powered insights about
              this deal.
            </Typography>
          </Box>
        ) : (
          <>
            {collapsed && messages.length > 3 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1.25,
                }}
              >
                <Button
                  size="small"
                  startIcon={<ExpandLessIcon />}
                  onClick={() => setCollapsed(false)}
                  aria-label="Show earlier messages"
                  sx={{
                    textTransform: 'none',
                    color: theme.secondary,
                    fontSize: 12,
                  }}
                >
                  Show {messages.length - 3} earlier messages
                </Button>
              </Box>
            )}
            {(collapsed ? messages.slice(-3) : messages).map((item) => {
              const isUser = item.sender === 'user';
              return (
                <Box key={item.id}>
                  <MessageBubble
                    message={item}
                    isUser={isUser}
                    onAnswerSelect={selectAnswer}
                    onActionPress={pressActionButton}
                    pressedActionButtonIds={pressedActionSet}
                  />
                  {item.isError && (
                    <Box sx={{ px: 1.5, mb: 0.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={retryLastMessage}
                        aria-label="Retry sending message"
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontSize: 12,
                          backgroundColor: theme.primary,
                          color: theme.textCTA,
                          '&:hover': {
                            backgroundColor: theme.primaryDark,
                          },
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}

        {/* Typing indicator */}
        {isWaitingForResponse && <TypingIndicator />}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Attachment preview strip */}
      {allAttachments.length > 0 && (
        <AttachmentPreview
          attachments={allAttachments}
          onRemove={removeAttachment}
        />
      )}

      {/* Chat input */}
      <ChatInput
        value={inputText}
        onChangeText={setInputText}
        onSend={sendMessage}
        sendDisabled={sendDisabled}
        onAttachPress={handleAttachPress}
        pendingPhotoCount={allAttachments.length}
      />

      {/* Hidden file input for web attachment picking */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Top-level component (wraps inner in provider)
// ---------------------------------------------------------------------------

const NotesToHankChat: React.FC<NotesToHankChatProps> = ({
  dealId,
  dealInfo,
}) => {
  return (
    <NotesToHankProvider dealId={dealId}>
      <NotesToHankChatInner dealId={dealId} dealInfo={dealInfo} />
    </NotesToHankProvider>
  );
};

export default NotesToHankChat;
